import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  fullWidth?: boolean;
}

/**
 * Dialog component for modal dialogs and confirmations
 * 
 * Can be used in two ways:
 * 1. Simple mode with direct props: title, description, children, actions
 * 2. Composite mode with child components: DialogTitle, DialogContent, DialogActions
 */
const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  actions,
  size = 'md',
  className = '',
  fullWidth = false,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  };

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Create portal to render at the end of the body
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      <div
        ref={dialogRef}
        className={`${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all ${className}`}
        tabIndex={-1}
      >
        {/* Simple mode with direct props */}
        {title && (
          <div className="px-6 py-4">
            <h3
              className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
              id="dialog-title"
            >
              {title}
            </h3>
            {description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {description}
              </p>
            )}
            {children && <div className="mt-4">{children}</div>}
          </div>
        )}
        
        {/* Composite mode with child components */}
        {!title && children}
        
        {/* Footer actions for simple mode */}
        {actions && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2 rounded-b-lg">
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;